import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import { SelectOneSelect } from "@/components/property-selectors/SelectOneSelect";
import { useChangeTaskCustomAttributeMutation } from "@/mutations/task-mutations";
import type { Attribute } from "@/types/attribute";
import type { ColumnDef } from "../../ColumnModel";
import type { Row } from "../../RowModel";
import { useBulkTargetIds } from "../../useBulkTargetIds";

// Static function - doesn't need to be recreated
const getSortValue =
    (attributeId: number) =>
    ({ row }: { row: Row }) => {
        const customFieldValue = row.data.customAttributeValues.find((x: any) => x.id === attributeId);
        return customFieldValue?.value;
    };

// Static function - doesn't need to be recreated
const getGroupHeader = () => {
    throw new Error("Not implemented. Custom attributes cannot be grouped for now.");
};

// Memoized cell component
const SelectOneCell = memo(function SelectOneCell({
    row,
    attribute,
    onValueChange,
}: {
    row: Row;
    attribute: Attribute;
    onValueChange: (taskId: number, attributeId: number, value: string) => void;
}) {
    const customFieldValues = row.data.customAttributeValues || [];
    const customFieldValue = customFieldValues.find((x: any) => x.id === attribute.id);

    const handleChange = useCallback(
        (value: string) => {
            onValueChange(row.data.id, attribute.id as number, value);
        },
        [row.data.id, attribute.id, onValueChange]
    );

    return (
        <SelectOneSelect
            attributeId={attribute.id as number} // this is safe, we know it's a select one attribute so nothing builtin
            trigger={(content) => (
                <CellButton className="text-sm" disabled={row.data.isNewlyCreated}>
                    <div className="truncate">{content}</div>
                </CellButton>
            )}
            value={customFieldValue?.value}
            onChange={handleChange}
        />
    );
});

export default function useSelectOneColumnBuilder() {
    const { mutate } = useChangeTaskCustomAttributeMutation();
    const getTargetIds = useBulkTargetIds();

    // Stable mutation handler
    const handleValueChange = useCallback(
        (taskId: number, attributeId: number, value: string) => {
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    customAttributeId: attributeId,
                    value: value,
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean, attribute: Attribute): ColumnDef => {
                return {
                    id: attribute.id.toString(),
                    header: attribute.name,
                    isVisible: isVisible,
                    cssColDef: "200px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue(attribute.id as number),
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <SelectOneCell row={row} attribute={attribute} onValueChange={handleValueChange} />,
                };
            },
            [handleValueChange]
        ),
    };
}
